/**
 * ModifiersModal.js — Versión con UX táctil mejorada para POS
 *
 * Comportamiento por tipo de grupo:
 *
 *  • num === 1 (selección única) — AUTO-AVANCE
 *    El usuario toca una opción → queda seleccionada y el modal avanza
 *    automáticamente al siguiente grupo sin necesidad de presionar Aceptar.
 *    Layout: botones grandes en cuadrícula.
 *
 *  • num > 1  (multi-selección con cantidad fija)
 *    El usuario usa botones +/− para elegir cantidades y presiona Siguiente.
 *    Layout: lista con controles de cantidad.
 *
 *  • num === 0 (opcional, sin límite)
 *    Igual que multi-selección: +/− y Siguiente manual.
 *
 * No se modifica la lógica de confirmación ni los datos de salida.
 */

import React, { useState, useEffect, useRef } from "react";
import "../styles/ModifiersModal.css";

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const ModifiersModal = ({ modifiers, onClose, onAddModifiers }) => {
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [justSelected, setJustSelected] = useState(null); // feedback visual en auto-avance

  // ── Clasificar modificadores ───────────────────────────────────────────────
  const groupedModifiers = modifiers.filter(
    (mod) => Array.isArray(mod.items) && mod.items.length > 0 && mod.num !== null && !isNaN(parseInt(mod.num))
  );
  const flatModifiers = modifiers.filter(
    (mod) => !mod.items && mod.id && mod.name && mod.price
  );

  const isGroupedMode = groupedModifiers.length > 0;
  const currentGroup   = isGroupedMode ? groupedModifiers[currentStep] : null;
  const totalRequired  = currentGroup ? parseInt(currentGroup.num) : 0;

  // ── ¿Es selección única? (num === 1 → auto-avance) ────────────────────────
  const isSingleSelect = totalRequired === 1;

  const totalRequiredAll = groupedModifiers.reduce(
    (sum, group) => sum + parseInt(group.num), 0
  );

  // Inicializar array vacío para cada grupo al llegar a él
  useEffect(() => {
    if (currentGroup) {
      setSelectedModifiers((prev) => ({
        ...prev,
        [currentGroup.name]: prev[currentGroup.name] || [],
      }));
    }
    setJustSelected(null);
    setError(null);
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers de cantidad ────────────────────────────────────────────────────
  const getGroupQuantity = (groupName) => {
    const name = groupName ?? currentGroup?.name;
    return (selectedModifiers[name] || []).reduce((s, i) => s + i.quantity, 0);
  };

  const getTotalQuantity = () =>
    Object.values(selectedModifiers).reduce(
      (t, g) => t + g.reduce((s, i) => s + i.quantity, 0), 0
    );

  // ── Resumen de texto para mostrar debajo del nombre del producto ───────────
  const getSelectionSummary = () => {
    const lines = Object.entries(selectedModifiers).flatMap(([, items]) =>
      items.filter((i) => i.quantity > 0).map((i) => `${i.name}${i.quantity > 1 ? ` x${i.quantity}` : ""}`)
    );
    return lines.join(" · ") || "";
  };

  // ── Manejar cambio de cantidad (multi-selección) ───────────────────────────
  const handleQuantityChange = (item, change) => {
    setError(null);
    setSelectedModifiers((prev) => {
      const current   = prev[currentGroup.name] || [];
      const existing  = current.find((s) => s.id === item.id);
      const groupQty  = getGroupQuantity();
      let next;

      if (existing) {
        const newQty = existing.quantity + change;
        if (newQty <= 0) {
          next = current.filter((s) => s.id !== item.id);
        } else if (totalRequired > 0 && groupQty + change > totalRequired) {
          setError(`Máximo ${totalRequired} en este grupo.`);
          return prev;
        } else {
          next = current.map((s) => s.id === item.id ? { ...s, quantity: newQty } : s);
        }
      } else if (change > 0) {
        if (totalRequired > 0 && groupQty + change > totalRequired) {
          setError(`Máximo ${totalRequired} en este grupo.`);
          return prev;
        }
        next = [...current, {
          id:        item.id,
          name:      item.name,
          price:     parseFloat(item.price) || 0,
          quantity:  1,
          productid: item.productid || item.id,
          groupName: currentGroup.name,
        }];
      } else {
        return prev;
      }

      return { ...prev, [currentGroup.name]: next };
    });
  };

  // ── Manejar toque en modo SINGLE-SELECT → seleccionar + auto-avanzar ───────
  const handleSingleTap = (item) => {
    // Mostrar feedback visual inmediato
    setJustSelected(item.id);

    // Poner la selección en estado
    const newEntry = {
      id:        item.id,
      name:      item.name,
      price:     parseFloat(item.price) || 0,
      quantity:  1,
      productid: item.productid || item.id,
      groupName: currentGroup.name,
    };

    setSelectedModifiers((prev) => ({
      ...prev,
      [currentGroup.name]: [newEntry], // reemplaza (solo 1 permitido)
    }));

    // Auto-avanzar después de un pequeño delay (feedback táctil)
    setTimeout(() => {
      advanceStep({ ...selectedModifiers, [currentGroup.name]: [newEntry] });
    }, 160);
  };

  // ── Avanzar paso (reutilizado por auto-avance y botón Siguiente) ───────────
  const advanceStep = (overrideSelected) => {
    if (currentStep < groupedModifiers.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Último grupo → confirmar
      confirmWithState(overrideSelected ?? selectedModifiers);
    }
  };

  // ── Validar grupo actual (para multi-select) ───────────────────────────────
  const validateCurrentGroup = () => {
    const qty = getGroupQuantity();
    if (totalRequired === 0) return true;
    if (qty < totalRequired) {
      setError(`Debes seleccionar ${totalRequired} opción${totalRequired > 1 ? "es" : ""} en este grupo.`);
      return false;
    }
    return true;
  };

  // ── Botón Siguiente (solo visible en multi-select) ─────────────────────────
  const handleNext = () => {
    if (!validateCurrentGroup()) return;
    advanceStep();
  };

  // ── Retroceder ────────────────────────────────────────────────────────────
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // ── Confirmar con un estado de selecciones (puede ser override) ────────────
  const confirmWithState = (state) => {
    let finalModifiers = isGroupedMode
      ? Object.values(state).flat()
      : Object.values(state).filter(Boolean);

    finalModifiers = finalModifiers.flatMap((mod) =>
      Array(mod.quantity).fill(null).map(() => ({
        id:        mod.id,
        name:      mod.name,
        price:     mod.price,
        quantity:  1,
        productid: mod.productid,
        groupName: mod.groupName,
      }))
    );

    onAddModifiers(finalModifiers);
    onClose();
  };

  const handleConfirm = () => {
    if (totalRequiredAll > 0 && getTotalQuantity() < totalRequiredAll) {
      setError("Faltan selecciones en uno o más grupos.");
      return;
    }
    confirmWithState(selectedModifiers);
  };

  // ── Modificadores planos opcionales ───────────────────────────────────────
  const handleFlatSelect = (modifier) => {
    setError(null);
    setSelectedModifiers((prev) => {
      const was = prev[modifier.id] || false;
      return {
        ...prev,
        [modifier.id]: was
          ? false
          : { id: modifier.id, name: modifier.name, price: parseFloat(modifier.price) || 0, quantity: 1, productid: modifier.productid || modifier.id, groupName: "Opcional" },
      };
    });
  };

  const handleCancel = () => { onAddModifiers([]); onClose(); };
  const handleWithoutModifiers = () => { onAddModifiers([]); onClose(); };

  // ── Sin modificadores válidos ──────────────────────────────────────────────
  if (groupedModifiers.length === 0 && flatModifiers.length === 0) {
    return (
      <div className="mm-overlay">
        <div className="mm-modal">
          <div className="mm-header">
            <span className="mm-title">Sin modificadores</span>
            <button className="mm-btn-icon" onClick={onClose} aria-label="Cerrar">✕</button>
          </div>
          <p className="mm-error">No se encontraron modificadores válidos para este producto.</p>
          <div className="mm-footer">
            <button className="mm-btn mm-btn-ghost" onClick={onClose}>Cerrar</button>
            <button className="mm-btn mm-btn-primary" onClick={handleWithoutModifiers}>Añadir sin modificadores</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Sumario de pasos anteriores ───────────────────────────────────────────
  const summary = getSelectionSummary();

  // ── RENDER PRINCIPAL ──────────────────────────────────────────────────────
  return (
    <div className="mm-overlay" onClick={(e) => e.target === e.currentTarget && handleCancel()}>
      <div className="mm-modal">

        {/* Header */}
        <div className="mm-header">
          <div className="mm-header-left">
            {currentStep > 0 && (
              <button className="mm-btn-icon mm-btn-back" onClick={handleBack} aria-label="Atrás">
                ←
              </button>
            )}
          </div>
          <span className="mm-title">
            {isGroupedMode ? currentGroup?.name : "Opcionales"}
          </span>
          <button className="mm-btn-icon mm-btn-close" onClick={handleCancel} aria-label="Cancelar">
            ✕
          </button>
        </div>

        {/* Progress indicator (solo en modo agrupado) */}
        {isGroupedMode && groupedModifiers.length > 1 && (
          <div className="mm-progress">
            {groupedModifiers.map((g, idx) => (
              <React.Fragment key={g.name}>
                <div className={`mm-progress-dot ${idx < currentStep ? 'done' : idx === currentStep ? 'active' : ''}`}>
                  {idx < currentStep ? '✓' : idx + 1}
                </div>
                {idx < groupedModifiers.length - 1 && (
                  <div className={`mm-progress-line ${idx < currentStep ? 'done' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Instrucción + sumario de selecciones anteriores */}
        <div className="mm-instruction">
          {isSingleSelect
            ? "Toca una opción para continuar automáticamente"
            : totalRequired === 0
              ? "Selecciona las opciones que desees"
              : `Distribuye ${totalRequired} en total entre las opciones`
          }
        </div>
        {/* Contador de progreso para multi-select */}
        {!isSingleSelect && isGroupedMode && totalRequired > 0 && (
          <div className="mm-counter">
            <span className={`mm-counter-val ${getGroupQuantity() === totalRequired ? 'complete' : ''}`}>
              {getGroupQuantity()} / {totalRequired}
            </span>
            <span className="mm-counter-label">
              {getGroupQuantity() === totalRequired
                ? " — ¡Listo!"
                : ` — faltan ${totalRequired - getGroupQuantity()}`}
            </span>
          </div>
        )}
        {summary && (
          <div className="mm-summary">
            <span className="mm-summary-label">Seleccionado:</span> {summary}
          </div>
        )}

        {/* Error */}
        {error && <div className="mm-error">{error}</div>}

        {/* ─── Modo agrupado ─────────────────────────────────────────────── */}
        {isGroupedMode && currentGroup && (
          <>
            {/* SINGLE-SELECT: cuadrícula de botones grandes con auto-avance */}
            {isSingleSelect ? (
              <div className="mm-options-grid">
                {currentGroup.items.map((item) => {
                  const isSelected = (selectedModifiers[currentGroup.name] || [])
                    .some((s) => s.id === item.id);
                  const isJust = justSelected === item.id;
                  return (
                    <button
                      key={item.id}
                      className={`mm-option-tile ${isSelected ? "selected" : ""} ${isJust ? "just-picked" : ""}`}
                      onClick={() => handleSingleTap(item)}
                    >
                      {isJust && <span className="mm-check">✓</span>}
                      <span className="mm-tile-name">{item.name}</span>
                      {item.price > 0 && (
                        <span className="mm-tile-price">+${parseFloat(item.price).toFixed(2)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              /* MULTI-SELECT: lista con controles +/− */
              <div className="mm-options-list">
                {currentGroup.items.map((item) => {
                  const sel = (selectedModifiers[currentGroup.name] || []).find((s) => s.id === item.id);
                  const qty = sel ? sel.quantity : 0;
                  const atMax = totalRequired > 0 && getGroupQuantity() >= totalRequired;
                  return (
                    <div key={item.id} className={`mm-option-row ${qty > 0 ? "selected" : ""}`}>
                      <span className="mm-row-name">{item.name}</span>
                      {item.price > 0 && (
                        <span className="mm-row-price">+${parseFloat(item.price).toFixed(2)}</span>
                      )}
                      <div className="mm-qty-controls">
                        <button
                          className="mm-qty-btn"
                          onClick={() => handleQuantityChange(item, -1)}
                          disabled={qty === 0}
                          aria-label={`Quitar ${item.name}`}
                        >−</button>
                        <span className="mm-qty-val">{qty}</span>
                        <button
                          className="mm-qty-btn"
                          onClick={() => handleQuantityChange(item, 1)}
                          disabled={atMax}
                          aria-label={`Agregar ${item.name}`}
                        >+</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ─── Modo plano (modificadores opcionales) ─────────────────────── */}
        {!isGroupedMode && (
          <div className="mm-options-list">
            {flatModifiers.map((modifier) => {
              const isSelected = !!selectedModifiers[modifier.id];
              return (
                <div
                  key={modifier.id}
                  className={`mm-option-row mm-option-row-check ${isSelected ? "selected" : ""}`}
                  onClick={() => handleFlatSelect(modifier)}
                >
                  <span className={`mm-checkbox ${isSelected ? "checked" : ""}`}>
                    {isSelected ? "✓" : ""}
                  </span>
                  <span className="mm-row-name">{modifier.name}</span>
                  {modifier.price > 0 && (
                    <span className="mm-row-price">+${parseFloat(modifier.price).toFixed(2)}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer — botones de acción */}
        <div className="mm-footer">
          {/* "Sin modificadores" solo si el producto no tiene requeridos */}
          {totalRequiredAll === 0 && getTotalQuantity() === 0 && (
            <button className="mm-btn mm-btn-ghost" onClick={handleWithoutModifiers}>
              Sin modificadores
            </button>
          )}

          {/* En auto-avance (single-select) no se muestra Siguiente — avanza solo */}
          {!isSingleSelect && isGroupedMode && (
            <button
              className="mm-btn mm-btn-primary"
              onClick={handleNext}
              disabled={totalRequired > 0 && getGroupQuantity() < totalRequired}
            >
              {currentStep < groupedModifiers.length - 1 ? "Siguiente →" : "✓ Confirmar"}
            </button>
          )}

          {/* Modo plano: botón confirmar */}
          {!isGroupedMode && (
            <button className="mm-btn mm-btn-primary" onClick={handleConfirm}>
              ✓ Confirmar
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default ModifiersModal;
